import { FunctionFactory } from 'survey-core';
import { Goals } from './goals';
import { ActivityLevel } from './activity';
import { BodyFat } from './bodyfat';
import { calculateCalorieTarget } from '../features/calculations';
import { Experience } from './experience';
import { Gender } from './gender';
import { WeightUnit, getWeightPounds } from './weight';
import { Physiology } from './physiology';
import { NumberWorkouts } from './numberWorkouts';

enum CalorieTracking {
  NoIdea = 'NoIdea',
  EatingMore = 'EatingMore',
  EatingLess = 'EatingLess',
  PrettyClose = 'PrettyClose',
}

const caloriesQuestion = {
  name: 'calories',
  title: `We estimate you’ll need to eat <div class="highlight">{caloriesText}</div> calories a day. How close are you to this now?`,
  description: `{goalDescription}`,
  type: 'radiogroup',
  choices: [
    {
      value: CalorieTracking.NoIdea,
      text: `I have no idea`,
    },
    {
      value: CalorieTracking.EatingMore,
      text: `Definitely eating more`,
    },
    {
      value: CalorieTracking.EatingLess,
      text: `Definitely eating less`,
    },
    {
      value: CalorieTracking.PrettyClose,
      text: `Pretty close to that target`,
    },
  ],
  isRequired: true,
};

const goalCaloriesTextExpression = {
  name: 'goalCaloriesText',
  expression: "renderGoalCaloriesText({goals})",
};

const caloriesTextExpression = {
  name: 'caloriesText',
  expression: "renderCaloriesText({weightUnit}, {weight}, {bodyFat}, {activity}, {experience}, {goals}, {gender}, {numberWorkouts}, {physiology})",
};

function renderGoalCaloriesText(params: any[]): string {
  const goals = params[0] as Goals;
  switch (goals) {
    case Goals.LoseFat:
      return `you need to eat less`;
    case Goals.BuildMuscle:
      return `you need to eat slightly more`;
    case Goals.BuildMuscleAndLoseFat:
      return `you need to eat slightly less`;
  }
}

function renderGoalDescription(params: any[]): string {
  const goals = params[0] as Goals;
  switch (goals) {
    case Goals.LoseFat:
      return `This target fuels your weight loss—we’ll help you get there.`;
    case Goals.BuildMuscle:
      return `This target fuels your muscle growth—we’ll help you get there.`;
    case Goals.BuildMuscleAndLoseFat:
      return `This target is designed to help you lose fat while still gaining muscle.`;
  }
}

FunctionFactory.Instance.register('renderGoalDescription', renderGoalDescription);

const goalDescriptionExpression = {
  name: 'goalDescription',
  expression: "renderGoalDescription({goals})",
};

// round to the nearest 50
function roundToNearest50(num: number): number {
  return Math.round(num / 50) * 50;
}

function renderCaloriesText(params: any[]): string {
  if (params === undefined || params === null || !params[0] || !params[1] || !params[2] || !params[3] || !params[4] || !params[5] || !params[6] || !params[7]) return '';
  const weightUnit = params[0] as WeightUnit;
  const weightRaw = params[1] as number;
  const bodyFat = params[2] as BodyFat;
  const activityLevel = params[3] as ActivityLevel;
  const experience = params[4] as Experience;
  const goal = params[5] as Goals;
  const gender = params[6] as Gender;
  const nunberWorkouts = params[7] as NumberWorkouts;
  const physiology = params[8] as Physiology;

  const weightPounds = getWeightPounds(weightRaw, weightUnit);
  const target = calculateCalorieTarget(experience, goal, bodyFat, gender, weightPounds, activityLevel, nunberWorkouts, physiology);
  const roundedTarget = roundToNearest50(target);
  let targets = [roundedTarget - 100, roundedTarget + 100];
  // Ensure the minimum limit after considering the window
  targets[0] = Math.max(gender === Gender.Male || physiology === Physiology.Male ? 1500 : 1200, targets[0]);
  targets[1] = Math.max(targets[0] + 200, targets[1]);

  return `${targets[0]} - ${targets[1]}`
}

FunctionFactory.Instance.register('renderGoalCaloriesText', renderGoalCaloriesText);
FunctionFactory.Instance.register('renderCaloriesText', renderCaloriesText);

export {
  CalorieTracking,
  caloriesQuestion,
  goalCaloriesTextExpression,
  caloriesTextExpression,
  renderCaloriesText,
  goalDescriptionExpression,
}